// ABOUTME: Waits for uploaded CV operation items to finish asynchronous parsing.
// ABOUTME: Treats extracted data and parser errors as resolved upload outcomes.
export type ParserUploadItem = {
    jsonCV?: unknown;
    status?: {
        value?: string;
    };
};

type ParserUploadReadiness = "pending" | "ready";

type WaitForParserUploadResultsParams = {
    expectedItemCount: number;
    loadItems: (options: {
        signal?: AbortSignal;
        timeoutMs: number;
    }) => Promise<ParserUploadItem[]>;
    intervalMs?: number;
    maxAttempts?: number;
    maxDurationMs?: number;
    signal?: AbortSignal;
    wait?: (intervalMs: number, signal?: AbortSignal) => Promise<void>;
};

const DEFAULT_INTERVAL_MS = 3000;
const DEFAULT_MAX_ATTEMPTS = 40;
const DEFAULT_MAX_DURATION_MS = 120000;
const DEFAULT_REQUEST_TIMEOUT_MS = 5000;

export class ParserUploadTimeoutError extends Error {
    constructor() {
        super("Parser upload did not finish in time");
        this.name = "ParserUploadTimeoutError";
    }
}

const waitForInterval = (intervalMs: number, signal?: AbortSignal) =>
    new Promise<void>(resolve => {
        if (signal?.aborted) {
            resolve();
            return;
        }

        let timeoutId: number | undefined;
        const finish = () => {
            if (timeoutId !== undefined) {
                globalThis.clearTimeout(timeoutId);
            }
            signal?.removeEventListener("abort", finish);
            resolve();
        };

        timeoutId = globalThis.setTimeout(finish, intervalMs);
        signal?.addEventListener("abort", finish, { once: true });
    });

const isParserUploadItemResolved = (item: ParserUploadItem) =>
    Boolean(item.jsonCV) || item.status?.value === "error";

export const isRetryableParserUploadRequestError = (error: unknown) => {
    const status = (error as { response?: { status?: number } })?.response
        ?.status;

    return (
        status === undefined ||
        status === 408 ||
        status === 429 ||
        status >= 500
    );
};

export const getParserUploadReadiness = (
    items: ParserUploadItem[],
    expectedItemCount: number
): ParserUploadReadiness => {
    if (items.length < expectedItemCount) {
        return "pending";
    }

    return items.every(isParserUploadItemResolved) ? "ready" : "pending";
};

export const waitForParserUploadResults = async ({
    expectedItemCount,
    loadItems,
    intervalMs = DEFAULT_INTERVAL_MS,
    maxAttempts = DEFAULT_MAX_ATTEMPTS,
    maxDurationMs = DEFAULT_MAX_DURATION_MS,
    signal,
    wait = waitForInterval
}: WaitForParserUploadResultsParams): Promise<
    ParserUploadItem[] | undefined
> => {
    if (signal?.aborted) {
        return undefined;
    }

    const controller = new AbortController();
    let resolveCancellation = () => {};
    const cancellation = new Promise<undefined>(resolve => {
        resolveCancellation = () => resolve(undefined);
    });
    const cancel = () => {
        controller.abort();
        resolveCancellation();
    };
    signal?.addEventListener("abort", cancel, { once: true });

    let deadlineId: ReturnType<typeof globalThis.setTimeout> | undefined;
    const deadline = new Promise<never>((_resolve, reject) => {
        deadlineId = globalThis.setTimeout(() => {
            reject(new ParserUploadTimeoutError());
            controller.abort();
        }, maxDurationMs);
    });

    const poll = async (): Promise<ParserUploadItem[] | undefined> => {
        for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
            if (controller.signal.aborted) {
                return undefined;
            }

            try {
                const items = await loadItems({
                    signal: controller.signal,
                    timeoutMs: DEFAULT_REQUEST_TIMEOUT_MS
                });
                if (controller.signal.aborted) {
                    return undefined;
                }
                if (
                    getParserUploadReadiness(items, expectedItemCount) ===
                    "ready"
                ) {
                    return items;
                }
            } catch (error) {
                if (controller.signal.aborted) {
                    return undefined;
                }
                if (!isRetryableParserUploadRequestError(error)) {
                    throw error;
                }
            }

            if (attempt < maxAttempts - 1) {
                await wait(intervalMs, controller.signal);
            }
        }

        throw new ParserUploadTimeoutError();
    };

    try {
        return await Promise.race([poll(), deadline, cancellation]);
    } finally {
        if (deadlineId !== undefined) {
            globalThis.clearTimeout(deadlineId);
        }
        signal?.removeEventListener("abort", cancel);
        controller.abort();
    }
};
